import React, { useState } from "react";
import { types, Text, Image, Repeater } from "react-bricks/frontend";
import styled from "styled-components";
import { color } from "styles/colours";
import { H1 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import TextBlock from "./TextBlock";
import { mobile } from "components/templates/Breakpoints";

interface TextWithModalProps {
  showButtons?: boolean;
}

const TextWithModal: types.Brick<TextWithModalProps> = ({ showButtons }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Trigger onClick={() => setIsOpen(true)}>
        <Text
          propName="triggerText"
          placeholder="Click to open modal"
          renderBlock={({ children }) => <span>{children}</span>}
        />
      </Trigger>

      {isOpen && (
        <ModalOverlay onClick={() => setIsOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setIsOpen(false)}>&times;</CloseButton>
            <ContentSection>
              <TitleSection>
                <ImageWrapper>
                  <Image propName="image" alt="Modal Image" maxWidth={120} />
                </ImageWrapper>
                <TitleWrapper>
                  <Text
                    propName="title"
                    placeholder="Title"
                    renderBlock={({ children }) => <Title>{children}</Title>}
                  />
                  <Text
                    propName="subheader"
                    placeholder="Subheader"
                    renderBlock={({ children }) => <Subheader>{children}</Subheader>}
                  />
                </TitleWrapper>
              </TitleSection>
              <TextSection>
                <TextBlock
                  propName="text"
                  placeholder="This is where the content for the modal lives..."
                  fontSize="1rem"
                  lineHeight="1.5rem"
                />
              </TextSection>
            </ContentSection>
            {showButtons && (
              <ButtonSection>
                <Repeater propName="buttons" />
              </ButtonSection>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default TextWithModal;

TextWithModal.schema = {
  name: "text-with-modal",
  label: "Text with Modal",
  category: "Modals",
  repeaterItems: [
    {
      name: "buttons",
      itemType: "buttonstrip",
      itemLabel: "Button Strip",
      min: 0,
      max: 2,
    },
  ],
  getDefaultProps: () => ({
    triggerText: "Learn more",
    title: "Modal title",
    subheader: "Short subheader",
    text: "Some detailed text goes here.",
    image: {
      src: "/static/bricks/examples/speaker-card-image.png",
    },
    showButtons: false,
  }),
  sideEditProps: [
    {
      name: "showButtons",
      label: "Show Buttons?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "triggerText",
      label: "Trigger Text",
      type: types.SideEditPropType.Text,
    },
  ],
};

const Trigger = styled.div`
  cursor: pointer;
  color: ${color.P100};
  text-decoration: underline;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: ${color.white};
  width: 45.5rem;
  border-radius: 12px;
  text-align: center;
  position: relative;

  ${mobile} {
    width: 90%;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleSection = styled.div`
  display: flex;
  gap: 1.5rem;
  height: 7.5rem;
  margin: 2rem;

  ${mobile} {
    flex-direction: column;
    align-items: center;
    height: auto;
    margin-bottom: 0.5rem;
  }
`;

const ImageWrapper = styled.div`
  border-radius: 4.375rem;
  width: 7.5rem;
  height: 7.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 7.5rem;
    height: 7.5rem;
    border-radius: 50%;
    object-fit: cover;
  }

  ${mobile} {
    width: 12rem;
    height: 12rem;

    img {
      width: 12rem;
      height: 12rem;
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled(H1)`
  font-size: 2rem;
  line-height: 2.5rem;
  color: ${color.H100};
  font-weight: 700;
  margin: 1rem 0 0.5rem;
  text-align: left;

  ${mobile} {
    text-align: center;
    margin-top: 0;
  }
`;

const Subheader = styled(P1)`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  font-weight: 700;
  color: ${color.P100};
  padding-top: 0;
  text-align: left;

  ${mobile} {
    text-align: center;
  }
`;

const TextSection = styled.div`
  margin: 0 2rem 2rem;
  text-align: left;
  padding-top: 0;

  ${mobile} {
    text-align: center;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  font-size: 2rem;
  cursor: pointer;
`;

const ButtonSection = styled.div`
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;

  ${mobile} {
    padding: 0 2rem 1.5rem;
  }

  button {
    padding: 0.5rem;
    font-size: 1rem;
    line-height: 1.5rem;
    height: 2.5rem;
  }
`;
